import { Box } from '@factorialco/deprecated-design-system'
import { F0Card, F0Icon, F0TagList, F0TagRaw } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Calendar, People, Pin, StarFilled } from '@factorialco/f0-react/icons/app'
import {
  AtsDeleteJobPostingFavouriteDocument,
  AtsFavouriteJobPostingItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { statusColor, statusLabel } from 'modules/ats/components/JobStatusTag'

import './index.styles.css'

interface FavouriteJobCardProps {
  favouriteJob: AtsFavouriteJobPostingItemFragment
  deletingFavouriteJob: number | null
  onDeleteFavourite: (jobId: number | null) => void
}

export const FavouriteJobCard = ({
  favouriteJob,
  deletingFavouriteJob,
  onDeleteFavourite,
}: FavouriteJobCardProps) => {
  const { navigation } = useNavigation()
  const {
    access: { id: accessId },
  } = useCurrentSession()

  const [deleteAtsFavouriteJobPosting, { loading }] = useMutation(
    AtsDeleteJobPostingFavouriteDocument,
    {
      extractorFn: (data) => data.atsMutations.deleteAtsFavouriteJobPosting,
    }
  )

  const onDelete = async () => {
    onDeleteFavourite(favouriteJob.id)
    const result = await deleteAtsFavouriteJobPosting({
      variables: { id: favouriteJob.id, authorId: accessId },
      update(cache) {
        cache.evict({ id: cache.identify(favouriteJob) })
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    onDeleteFavourite(null)

    return handler
  }

  const {
    title,
    status,
    workplaceTypeLabel,
    location,
    id: jobId,
    publishedAt,
    applicationphasesConnection,
  } = favouriteJob.atsJobPosting

  const phases = applicationphasesConnection.nodes.filter(
    (phase) => phase?.activeApplicationsCount && phase?.activeApplicationsCount > 0
  )

  return (
    <Box position="relative">
      <F0Card
        title={title}
        link={navigation.ats.jobs.jobsId.applications.fullPath({
          jobId,
        })}
        metadata={[
          // @ts-expect-error StatusMetadata requires icon but it works without it
          {
            property: {
              type: 'status',
              value: {
                label: statusLabel(status),
                status: statusColor(status),
              },
            },
          },
          {
            property: {
              type: 'text',
              value: `${location?.name} ${workplaceTypeLabel ? `(${workplaceTypeLabel})` : ''}`,
            },
            icon: Pin,
          },
          {
            property: {
              type: 'text',
              value: publishedAt ? dateBackendDateTimeToRelativeTime(publishedAt) : '-',
            },
            icon: Calendar,
          },
        ]}
      >
        <Box gap="s6" flexDirection="row" alignItems="center">
          <F0Icon icon={People} className="text-f1-icon" size="md" />
          {phases.length > 0 ? (
            <F0TagList
              type="dot"
              tags={phases.map((phase) => ({
                text: phase.activeApplicationsCount?.toString() ?? '0',
                color: phaseColor(phase?.hiringStage?.name ?? 'default'),
                description: phase?.name,
              }))}
            />
          ) : (
            <F0TagRaw text={i18n.t('ats.jobs.favourites.no_candidates')} />
          )}
        </Box>
      </F0Card>

      <Box
        position="absolute"
        background="grey000White"
        className="FavouriteList_starButtonStyle"
        alignItems="center"
        justifyContent="center"
        borderRadius={{
          all: 'abs006',
        }}
        style={{
          top: '16px',
          right: '16px',
          cursor: 'pointer',
        }}
        onClick={onDelete}
      >
        <F0Icon icon={StarFilled} color="selected" size="lg" />
      </Box>

      {(deletingFavouriteJob === favouriteJob.id || loading) && (
        <Box
          position="absolute"
          background="grey000White"
          style={{
            inset: '0px',
            opacity: '0.5',
          }}
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="large" />
        </Box>
      )}
    </Box>
  )
}
